
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { MoreHorizontal, User, Users, HeartHandshake, Trash, Shield, Mail, Phone } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import AddMemberForm from './AddMemberForm';

// Sample data for bubble members
const members = [
  { 
    id: '1', 
    name: 'Sarah Johnson', 
    phone: '(555) 123-4567',
    role: 'care-recipient',
    type: 'primary',
    status: 'active',
    isAdmin: false,
    image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fHNlbmlvcnxlbnwwfHwwfHx8MA%3D%3D'
  },
  { 
    id: '2', 
    name: 'Robert Smith', 
    phone: '(555) 234-5678',
    role: 'family',
    type: 'admin',
    status: 'active',
    isAdmin: true,
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fG1hbnxlbnwwfHwwfHx8MA%3D%3D'
  },
  { 
    id: '3', 
    name: 'Emma Wilson', 
    phone: '(555) 345-6789',
    role: 'professional',
    type: 'member',
    status: 'active',
    isAdmin: false,
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fHdvbWFufGVufDB8fDB8fHww'
  },
  { 
    id: '4', 
    name: 'Michael Davis', 
    phone: '(555) 456-7890',
    role: 'family',
    type: 'member',
    status: 'active',
    isAdmin: false,
    image: 'https://images.unsplash.com/photo-1480429370139-e0132c086e2a?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bWFufGVufDB8fDB8fHww'
  },
  { 
    id: '5', 
    name: 'Linda Chen', 
    phone: '(555) 567-8901',
    role: 'volunteer',
    type: 'member',
    status: 'pending',
    isAdmin: false,
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fHdvbWFufGVufDB8fDB8fHww'
  },
  { 
    id: '6', 
    name: 'James Taylor', 
    phone: '(555) 678-9012',
    role: 'professional',
    type: 'member',
    status: 'active',
    isAdmin: false,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fG1hbnxlbnwwfHwwfHx8MA%3D%3D'
  },
];

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'family':
      return <Users className="h-4 w-4" />;
    case 'professional':
      return <HeartHandshake className="h-4 w-4" />;
    case 'volunteer':
      return <HeartHandshake className="h-4 w-4" />;
    default:
      return <User className="h-4 w-4" />;
  }
};

const getRoleBadge = (role: string) => {
  switch (role) {
    case 'family':
      return <Badge variant="outline" className="bg-tech-50 text-tech-800">Family</Badge>;
    case 'professional':
      return <Badge variant="outline" className="bg-blue-50 text-blue-800">Professional</Badge>;
    case 'volunteer':
      return <Badge variant="outline" className="bg-green-50 text-green-800">Volunteer</Badge>;
    case 'care-recipient':
      return <Badge variant="outline" className="bg-indigo-50 text-indigo-800">Care Recipient</Badge>;
    default:
      return null;
  }
};

const BubbleMembers = () => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  
  const handleRemoveMember = (member: any) => {
    toast.success(`${member.name} removed from bubble`, {
      description: "They will no longer have access to this care bubble",
    });
  };
  
  const handleMakeAdmin = (member: any) => {
    toast.success(`${member.name} is now an admin`, {
      description: "They can now manage members and the bubble wallet",
    });
  };
  
  const handleResendInvite = (member: any) => {
    toast.success(`Invitation resent to ${member.name}`, {
      description: "A new invitation has been sent",
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Bubble Members</CardTitle>
            <CardDescription>
              Manage the members in this care bubble
            </CardDescription>
          </div>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>Add Member</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Bubble Member</DialogTitle>
                <DialogDescription>
                  Add someone to your care bubble. They'll receive an invitation to join.
                </DialogDescription>
              </DialogHeader>
              <AddMemberForm onClose={() => setAddDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors group">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={member.image} alt={member.name} className="object-cover" />
                  <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{member.name}</h4>
                    {member.isAdmin && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800">
                        <Shield className="h-3 w-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                    {member.status === 'pending' && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-600">Pending</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    <span>{member.phone}</span>
                    <span className="text-gray-300">•</span>
                    {getRoleBadge(member.role)}
                  </div>
                </div>
              </div>
              
              {member.role !== 'care-recipient' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {!member.isAdmin && (
                      <DropdownMenuItem onClick={() => handleMakeAdmin(member)}>
                        <Shield className="h-4 w-4 mr-2" />
                        Make Admin
                      </DropdownMenuItem>
                    )}
                    {member.status === 'pending' && (
                      <DropdownMenuItem onClick={() => handleResendInvite(member)}>
                        <Mail className="h-4 w-4 mr-2" />
                        Resend Invite
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => handleRemoveMember(member)}
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BubbleMembers;
